import React, { useMemo, useState } from "react";
import { Alert, Button, Container, Form, Spinner, Table } from "react-bootstrap";
import API from "../../services/api";
import "../EmployeeAccounts.css";

const fmtMins = (mins) => {
  if (!mins && mins !== 0) return "-";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
};

const fmtMoney = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const AccountsWorktime = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    setMessage("");
    try {
      const res = await API.get("/employee/accounts/worktime-summary", { params: { from, to } });
      setData(res.data);
      if (Array.isArray(res.data)) {
        if (!res.data.length) setMessage("No records found.");
      } else if (!res.data?.records?.length) {
        setMessage("No records found.");
      }
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to load work summary.");
    } finally {
      setLoading(false);
    }
  };

  const summaryCards = useMemo(() => {
    if (!data || Array.isArray(data)) return [];
    const salary = Number(data.workProfile?.salaryMonthly || 0);
    const overtimePay = Number(data.totals?.overtimePay || 0);
    const latePenalty = Number(data.totals?.latePenalty || 0);
    const finalPay = salary + overtimePay - latePenalty;
    return [
      { label: "Salary", value: fmtMoney(salary) },
      { label: "Total Days", value: data.totals?.totalDays || 0 },
      { label: "Work Time", value: fmtMins(data.totals?.workMinutes) },
      { label: "Overtime", value: fmtMins(data.totals?.overtimeMinutes) },
      { label: "Late Penalty", value: fmtMoney(latePenalty) },
      { label: "Final Pay", value: fmtMoney(finalPay) },
    ];
  }, [data]);

  return (
    <Container className="accounts-page accounts-worktime-page">
      <section className="accounts-worktime-shell">
        <header className="accounts-worktime-header">
          <div>
            <h2>Worktime & Overtime Summary</h2>
            <p>Review attendance-driven compensation, overtime, and penalties for selected dates.</p>
          </div>
          <div className="accounts-worktime-actions">
            <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <Button className="accounts-btn-primary" onClick={fetchSummary} disabled={loading}>
              {loading ? "Loading..." : "Fetch Summary"}
            </Button>
          </div>
        </header>

        {message && <Alert variant="info" className="mx-4 mb-0">{message}</Alert>}

        {loading && (
          <div className="accounts-worktime-loading">
            <Spinner animation="border" />
          </div>
        )}

        {!loading && summaryCards.length > 0 && (
          <section className="accounts-worktime-stats">
            {summaryCards.map((card) => (
              <article key={card.label}>
                <span>{card.label}</span>
                <strong>{card.value}</strong>
              </article>
            ))}
          </section>
        )}

        {!loading && data && Array.isArray(data) && (
          <section className="accounts-worktime-tablecard">
            <div className="accounts-worktime-tablewrap">
              <Table responsive className="accounts-worktime-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Mobile</th>
                    <th>Salary</th>
                    <th>Total Days</th>
                    <th>Work Time</th>
                    <th>Overtime</th>
                    <th>Overtime Pay</th>
                    <th>Late</th>
                    <th>Late Penalty</th>
                    <th>Final Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((r) => {
                    const salary = Number(r.workProfile?.salaryMonthly || 0);
                    const overtimePay = Number(r.totals?.overtimePay || 0);
                    const latePenalty = Number(r.totals?.latePenalty || 0);
                    const finalPay = salary + overtimePay - latePenalty;
                    return (
                      <tr key={r.employeeId}>
                        <td>{r.fullName || "-"}</td>
                        <td>{r.mobile || "-"}</td>
                        <td>{fmtMoney(salary)}</td>
                        <td>{r.totals?.totalDays || 0}</td>
                        <td>{fmtMins(r.totals?.workMinutes)}</td>
                        <td>{fmtMins(r.totals?.overtimeMinutes)}</td>
                        <td>{fmtMoney(overtimePay)}</td>
                        <td>{fmtMins(r.totals?.lateMinutes)}</td>
                        <td>{fmtMoney(latePenalty)}</td>
                        <td>{fmtMoney(finalPay)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </section>
        )}

        {!loading && data && !Array.isArray(data) && (
          <section className="accounts-worktime-tablecard">
            <div className="accounts-worktime-tablewrap">
              <Table responsive className="accounts-worktime-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Punch In</th>
                    <th>Punch Out</th>
                    <th>Work</th>
                    <th>Late</th>
                    <th>Overtime</th>
                    <th>Overtime Pay</th>
                    <th>Late Penalty</th>
                    <th>Final Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.records || []).map((r) => {
                    const overtimePay = Number(r.overtimePay || 0);
                    const latePenalty = Number(r.latePenalty || 0);
                    const salary = Number(data.workProfile?.salaryMonthly || 0);
                    const finalPay = salary + overtimePay - latePenalty;
                    return (
                      <tr key={r._id}>
                        <td>{r.date ? new Date(r.date).toLocaleDateString("en-IN") : "-"}</td>
                        <td>{r.punchIn?.time ? new Date(r.punchIn.time).toLocaleString("en-IN") : "-"}</td>
                        <td>{r.punchOut?.time ? new Date(r.punchOut.time).toLocaleString("en-IN") : "-"}</td>
                        <td>{fmtMins(r.workMinutes)}</td>
                        <td>{fmtMins(r.lateMinutes)}</td>
                        <td>{fmtMins(r.overtimeMinutes)}</td>
                        <td>{fmtMoney(overtimePay)}</td>
                        <td>{fmtMoney(latePenalty)}</td>
                        <td>{fmtMoney(finalPay)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </section>
        )}
      </section>
    </Container>
  );
};

export default AccountsWorktime;
